import React, { MouseEvent, ReactNode } from 'react';
import cx from 'classnames';

import Star from 'components/star/star';
import { getInitials, getPlayerPositionCode } from 'pages/game-center/home/dashboard/helpers';
import PlayerHeadshot from 'components/player-headshot';
import { getPlayerDisplayName } from 'utils/helpers/player-profile-utils';

import styles from '../search-dropdown.module.css';

/** The star of a row: shown only when it is passed */
export type RowFavorite = {
    isActive: boolean;
    onToggle: () => void;
};

type DropdownRowProps = {
    avatar: ReactNode;
    name: string;
    badge?: string;
    meta?: string | null;
    favorite?: RowFavorite;
    onClick: () => void;
};

const DropdownRow = ({ avatar, name, badge, meta, favorite, onClick }: DropdownRowProps) => {
    const handleStarClick = (event: MouseEvent) => {
        // The row opens the item, the star only toggles the favorite
        event.stopPropagation();
        favorite?.onToggle();
    };

    return (
        <div className={cx(styles.row, { [styles.rowWithStar]: !!favorite })} onClick={onClick}>
            {avatar}
            <div className={styles.copy}>
                <div className={styles.nameRow}>
                    <span className={styles.name}>{name}</span>
                    {badge ? <span className={styles.positionBadge}>{badge}</span> : null}
                </div>
                {meta ? <span className={styles.meta}>{meta}</span> : null}
            </div>
            {favorite ? (
                <button type="button" className={styles.starButton} onClick={handleStarClick}>
                    <Star active={favorite.isActive} height={18} width={18} />
                </button>
            ) : null}
        </div>
    );
};

export type PlayerRowData = {
    firstNameEn?: string | null;
    lastNameEn?: string | null;
    playerPosition?: string | null;
};

type PlayerRowProps = {
    player: PlayerRowData;
    photoUrl: string | null;
    meta?: string | null;
    favorite?: RowFavorite;
    onClick: () => void;
};

export const PlayerRow = ({ player, photoUrl, meta, favorite, onClick }: PlayerRowProps) => {
    const name = getPlayerDisplayName(player.firstNameEn, player.lastNameEn);

    return (
        <DropdownRow
            avatar={
                <div className={styles.avatar}>
                    <PlayerHeadshot
                        firstName={player.firstNameEn}
                        lastName={player.lastNameEn}
                        name={name}
                        photoUrl={photoUrl}
                        size={40}
                        fillContainer
                    />
                </div>
            }
            name={name}
            badge={getPlayerPositionCode(player)}
            meta={meta}
            favorite={favorite}
            onClick={onClick}
        />
    );
};

type TeamRowProps = {
    name?: string | null;
    logo?: string | null;
    meta?: string | null;
    favorite?: RowFavorite;
    onClick: () => void;
};

export const TeamRow = ({ name, logo, meta, favorite, onClick }: TeamRowProps) => (
    <DropdownRow
        avatar={<div className={styles.teamLogo}>{logo ? <img alt="" src={logo} /> : getInitials(name)}</div>}
        name={name || '-'}
        meta={meta}
        favorite={favorite}
        onClick={onClick}
    />
);
